import { useCallback, useEffect, useRef, useState } from "react";
import { selectAuth, updateExpiresAt } from "src/features/authSlice";
import { useAppDispatch, useAppSelector } from "src/app/hooks";
import { callApi, HttpMethod } from "src/services/apiService";

/**
 * Drives the idle-session modal against the server's authoritative expiry
 * timestamp rather than a local setTimeout fire-time.
 *
 * Why: browsers throttle setTimeout/setInterval on hidden tabs (Chrome down
 * to ~1/min after 5 minutes of backgrounding). A relative-duration timer
 * drifts badly, so the user either gets logged out late or sees the countdown
 * modal with a frozen clock. This hook instead stores an absolute expiry
 * (Date.now() + ttl computed by the server) and lets a 1-second watchdog
 * compare wall-clock on every tick. When the tab wakes, the very next tick
 * reads a fresh Date.now() and decides correctly, regardless of how many
 * ticks the browser suppressed while hidden.
 *
 * The same pattern is used by the Razor idle partial at
 * Views/Shared/_SessionExpireNotification.cshtml.
 */
// "authorized": server extended the session. Client should trust new expiresAt.
// "unauthorized": server rejected. Caller should log out. (Note: callApi itself
//                 already redirects on 401, so this mostly covers custom non-Authorized
//                 responses that do not 401.)
// "transient":   network / 5xx error. Don't log out — the existing expiresAt
//                 still applies and the watchdog will force logout at the original
//                 deadline if the outage outlasts the session.
export type RefreshSessionResult = "authorized" | "unauthorized" | "transient";

interface UseInactivityResult {
  // True once wall-clock time is within COUNTDOWN_WINDOW_MS of expiry.
  // The caller shows the "about to expire" modal off this signal.
  isInactive: boolean;
  // True once wall-clock time has passed expiry. The caller forces logout
  // on this signal. Kept separate from isInactive because a caller can
  // dismiss the modal (e.g. race-avoidance on the "Stay signed in" button)
  // without the modal's countdown effect running — if the hard deadline
  // were only enforced in that effect the session could silently live on
  // until the next 401 from the server.
  isExpired: boolean;
  // Explicit re-sync, used by the "Yes, keep me signed in" button.
  // Return value tells the caller whether to force-logout, keep the modal
  // hidden and retry later, or carry on.
  refreshSession: () => Promise<RefreshSessionResult>;
}

const COUNTDOWN_WINDOW_MS = 60 * 1000; // Modal appears this long before expiry
const WATCHDOG_INTERVAL_MS = 1000;
const KEEPALIVE_DEBOUNCE_MS = 5000;

const useInactivity = (): UseInactivityResult => {
  const { isAuthenticated, expiresAt } = useAppSelector(selectAuth);
  const dispatch = useAppDispatch();

  // Fast-read mirror of expiresAt for the setInterval closure below.
  // Using a ref avoids re-arming the watchdog on every expiresAt change.
  const expiresAtMsRef = useRef<number | null>(null);
  const [isInactive, setIsInactive] = useState(false);
  const [isExpired, setIsExpired] = useState(false);

  // Mirror Redux -> ref whenever the server pushes a new expiry (login or keepalive).
  useEffect(() => {
    if (expiresAt) {
      const ms = new Date(expiresAt).getTime();
      if (!Number.isNaN(ms)) {
        expiresAtMsRef.current = ms;
        // Fresh value -> user is no longer "about to expire" even if they
        // transiently were before the keepalive response landed.
        setIsInactive(false);
      }
    } else {
      expiresAtMsRef.current = null;
    }
  }, [expiresAt]);

  // Talks to the server. Response carries the new absolute expiry which we
  // dispatch to Redux so the whole app re-syncs in one place.
  const sendKeepAlive = useCallback(async (): Promise<RefreshSessionResult> => {
    if (!isAuthenticated) return "transient";
    try {
      const response = await callApi("users/IsAuthenticated", HttpMethod.GET);
      // callApi redirects to /login on 401 before returning, so a defined
      // response with message !== "Authorized" means some other non-authorized
      // case that the caller should treat as unauthorized.
      if (!response || response.message !== "Authorized") return "unauthorized";
      if (typeof response.expiresAt === "string") {
        const ms = new Date(response.expiresAt).getTime();
        if (!Number.isNaN(ms)) {
          expiresAtMsRef.current = ms;
          dispatch(updateExpiresAt(response.expiresAt));
          setIsInactive(false);
        }
      }
      return "authorized";
    } catch {
      // Network error or 5xx. Don't force logout; the existing expiresAt is
      // still valid and the watchdog will handle the original deadline.
      return "transient";
    }
  }, [isAuthenticated, dispatch]);

  // Wire up activity + visibility listeners. All roads lead to a debounced
  // keepalive which re-anchors expiresAt from the server.
  useEffect(() => {
    if (!isAuthenticated) return;

    let debounceTimer: number | null = null;
    const debouncedKeepAlive = () => {
      if (debounceTimer !== null) window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(() => {
        debounceTimer = null;
        sendKeepAlive();
      }, KEEPALIVE_DEBOUNCE_MS);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") sendKeepAlive();
    };

    window.addEventListener("click", debouncedKeepAlive);
    window.addEventListener("keydown", debouncedKeepAlive);
    window.addEventListener("mousemove", debouncedKeepAlive);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Seed expiresAt if login response did not carry it (e.g. user reloaded
    // the page and PrivateRoute rehydrated from cookie but server did not
    // include expiresAt on that rehydrate path).
    if (expiresAtMsRef.current === null) {
      sendKeepAlive();
    }

    return () => {
      if (debounceTimer !== null) window.clearTimeout(debounceTimer);
      window.removeEventListener("click", debouncedKeepAlive);
      window.removeEventListener("keydown", debouncedKeepAlive);
      window.removeEventListener("mousemove", debouncedKeepAlive);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isAuthenticated, sendKeepAlive]);

  // Watchdog: 1-second setInterval that compares wall-clock to the expiry ref.
  // Immune to throttling: on tab wake the next tick reads a fresh Date.now()
  // and decides correctly even if the browser suppressed hundreds of ticks
  // while the tab was hidden.
  //
  // Hard expiry (remaining <= 0) is enforced here, independent of the modal.
  // Earlier revisions of this hook only surfaced isInactive and relied on the
  // modal countdown to fire logout at zero — but if the caller dismissed the
  // modal (e.g. after a transient keepalive failure) the countdown effect
  // would stop running and the session could silently outlive its deadline
  // until the next API call 401'd.
  useEffect(() => {
    if (!isAuthenticated) return;

    const watchdog = window.setInterval(() => {
      const exp = expiresAtMsRef.current;
      if (exp === null) return;
      const remaining = exp - Date.now();
      if (remaining <= 0) {
        setIsExpired(true);
      } else if (remaining <= COUNTDOWN_WINDOW_MS) {
        setIsInactive(true);
      }
    }, WATCHDOG_INTERVAL_MS);

    return () => window.clearInterval(watchdog);
  }, [isAuthenticated]);

  return { isInactive, isExpired, refreshSession: sendKeepAlive };
};

export default useInactivity;
